// The VIP Quest: FFTBG's season-long race to champion the most DIFFERENT classes. Gender
// doesn't matter and monsters count, so there are 75 to collect. Everything here reads the
// champion history the site already ships (champHistory.json: r = reigns
// [startMs, color, len, [[name, class, g, sign]×4]], p = player → reign indices), so the
// standings need no build step. The community's tracker is meatb.one/fftbg/leaderboard.html;
// on 2026-10-01 these counts matched its Season 29 board everywhere except the reigning
// champion, whom it adds only once they're dethroned. A reign counts for the season it was
// dethroned in, as FFTBG files it (see reignEnds).

export const VIP_HUMANS = ['Squire', 'Chemist', 'Knight', 'Archer', 'Monk', 'Priest', 'Wizard', 'Time Mage', 'Summoner', 'Thief',
  'Mediator', 'Oracle', 'Geomancer', 'Lancer', 'Samurai', 'Ninja', 'Calculator', 'Dancer', 'Bard', 'Mime'];
// Bestiary order, family by family (the families of the Players page's Monster Families board).
export const VIP_MONSTERS = ['Chocobo', 'Black Chocobo', 'Red Chocobo', 'Goblin', 'Black Goblin', 'Gobbledeguck',
  'Bomb', 'Grenade', 'Explosive', 'Red Panther', 'Coeurl', 'Vampire', 'Pisco Demon', 'Squidraken', 'Mindflayer',
  'Skeleton', 'Draugr', 'Reaper', 'Ghoul', 'Ghost', 'Revenant', 'Floating Eye', 'Ahriman', 'Plague',
  'Juravis', 'Iron Hawk', 'Cockatrice', 'Swine', 'Porky', 'Wild Boar', 'Dryad', 'Treant', 'Taiju',
  'Bull Demon', 'Minotaur', 'Sekhret', 'Malboro', 'Ochu', 'Great Malboro', 'Behemoth', 'King Behemoth', 'Dark Behemoth',
  'Dragon', 'Blue Dragon', 'Red Dragon', 'Holy Dragon', 'Wyvern', 'Hydra', 'Tiamat', 'Archaic Demon', 'Ultima Demon',
  'Apanda', 'Byblos', 'Steel Giant', 'Serpentarius'];
export const VIP_CLASSES = [...VIP_HUMANS, ...VIP_MONSTERS];
const HUMAN = new Set(VIP_HUMANS), KNOWN = new Set(VIP_CLASSES);
// The gendered count takes each human class as male and female, except the one-gender ones.
export const ONE_GENDER = { Bard: 'M', Dancer: 'F' };
export const VIP_GENDERED_TOTAL = VIP_HUMANS.reduce((n, c) => n + (ONE_GENDER[c] ? 1 : 2), 0);   // 38

const norm = (s) => String(s).toLowerCase().replace(/[\s_]+/g, ' ').trim();

// Which unit on a reign's roster is each credited player's. Mostly the names match. A player
// who has since renamed is credited under their new handle while the roster keeps the old
// one, so once the names are matched, a lone leftover player takes the lone leftover unit.
// More than one leftover is ambiguous and skipped rather than guessed.
export function reignOwners(units, players) {
  const out = new Map(), freeUnits = units.map((_, i) => i), freePlayers = [];
  for (const p of players) {
    const k = freeUnits.findIndex((i) => norm(units[i][0]) === norm(p));
    if (k < 0) freePlayers.push(p);
    else { out.set(p, units[freeUnits[k]]); freeUnits.splice(k, 1); }
  }
  if (freePlayers.length === 1 && freeUnits.length === 1) out.set(freePlayers[0], units[freeUnits[0]]);
  return out;
}

// When each reign ended, for filing it under a season. FFTBG files a champion under the
// season it was DETHRONED in: the API's champion records carry a Season that equals the
// season of their Defeat for all 28,271 of them (checked 2026-10-01; meatb.one's season
// boundaries follow from it). So a reign won just before a ToC and lost just after counts
// for the new season (Zelgadissan's Red Chocobo, champion #26279: won Apr 11 12:09, lost
// 20:43, after the Season 28 boundary). A reign ends in the tournament its successor won
// the belt in, which champReignMeta's wd gives (successor start − wd). Without it, the
// successor's first defense stands in, and lands on the wrong side of the ToC for a
// season's last champion. The newest reign hasn't ended, so it counts for the current
// season. champHistory.r runs newest first.
export function reignEnds(champHistory, reignMeta) {
  const r = champHistory.r, wd = (reignMeta && reignMeta.wd) || [];
  return r.map((_, i) => (i === 0 ? Infinity : r[i - 1][0] - (wd[i - 1] || 0)));
}

// The tournament reign i was lost in (its successor's win), for linking to; 0 when there's
// none to link: the newest reign is still going, and a successor with no clean win (wd 0:
// pre-feed, or the roster changed with no dethroning) leaves no bracket.
export function reignLostIn(champHistory, reignMeta, i) {
  const wd = reignMeta && reignMeta.wd;
  if (!i || !wd || !wd[i - 1]) return 0;
  return champHistory.r[i - 1][0] - wd[i - 1];
}

// Reigns the API never numbered as champions although they're older than ones it did: roster
// blips that sat in the champion slot for a tournament while the API kept one unbroken reign
// (seven by 2026-10-01, among them two teams of four Samurai). Champion numbers run by defeat
// time without gaps, so an unnumbered reign older than the newest numbered one is a blip,
// while the newest reigns may just be waiting for their number. FFTBG doesn't count blips,
// and with them left out, every Season 17–28 count matches meatb.one's.
export function blipReigns(reignMeta) {
  const cn = (reignMeta && reignMeta.cn) || [];
  const newest = cn.findIndex((x) => x > 0), out = new Set();
  if (newest >= 0) for (let i = newest + 1; i < cn.length; i++) if (!cn[i]) out.add(i);
  return out;
}

// Rows best-first: count = distinct classes (the VIP number), humans / monsters / gendered
// = the same split three ways, titles = reigns won, reachedAt = when they got their newest
// class (a tie goes to whoever got there first). cls maps each class to { titles, first,
// best (longest reign), g: Set of 'M' | 'F' | 'X' }.
// season 0 = all time. seasonOf maps a time (ms) to its season (lib/seasons.js); reignMeta
// is the champReignMeta side-file, index-aligned with champHistory (see reignEnds).
export function vipStandings(champHistory, season, seasonOf, reignMeta) {
  const { r: reigns, p: players } = champHistory;
  const ends = season ? reignEnds(champHistory, reignMeta) : null;
  const blips = blipReigns(reignMeta);
  const owners = new Map();   // reign index → the players credited with it
  for (const name in players) {
    for (const i of players[name]) {
      if (blips.has(i) || (season && seasonOf(ends[i]) !== season)) continue;
      const a = owners.get(i);
      if (a) a.push(name); else owners.set(i, [name]);
    }
  }
  const rows = new Map();
  for (const [i, names] of owners) {
    const [start, , len, units] = reigns[i];
    for (const [name, u] of reignOwners(units, names)) {
      if (!KNOWN.has(u[1])) continue;
      let row = rows.get(name);
      if (!row) rows.set(name, (row = { name, cls: {} }));
      const c = row.cls[u[1]] || (row.cls[u[1]] = { titles: 0, first: start, best: 0, g: new Set() });
      c.titles++;
      if (start < c.first) c.first = start;
      if (len > c.best) c.best = len;
      c.g.add(u[2]);
    }
  }
  for (const row of rows.values()) {
    let humans = 0, monsters = 0, gendered = 0, titles = 0, reachedAt = 0;
    for (const cls in row.cls) {
      const c = row.cls[cls];
      titles += c.titles;
      if (c.first > reachedAt) reachedAt = c.first;
      if (!HUMAN.has(cls)) { monsters++; continue; }
      humans++;
      gendered += ONE_GENDER[cls] ? 1 : (c.g.has('M') ? 1 : 0) + (c.g.has('F') ? 1 : 0);
    }
    Object.assign(row, { count: humans + monsters, humans, monsters, gendered, titles, reachedAt });
  }
  return [...rows.values()].sort(rankBy('count'));
}

// Best-first on one of the counts; ties go to whoever got there first, then by name.
export const rankBy = (key) => (a, b) => b[key] - a[key] || a.reachedAt - b.reachedAt || a.name.localeCompare(b.name);

// A player's row in standings, matched like the rosters are (case, spaces, underscores).
export function vipRowFor(standings, name) {
  const k = norm(name || '');
  return (k && standings.find((r) => norm(r.name) === k)) || null;
}

// The classes a row is still missing, in board order (all 75 for a player with no row).
export function vipMissing(row) {
  return VIP_CLASSES.filter((c) => !(row && row.cls[c]));
}
